import React, { useState, useEffect, useRef } from 'react';

const DisorderRegionSearch = ({ data }) => {
  const [disorders, setDisorders] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedDisorders, setSelectedDisorders] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [results, setResults] = useState(null);
  
  // Search and dropdown states for disorders
  const [disorderSearchTerm, setDisorderSearchTerm] = useState('');
  const [isDisorderDropdownOpen, setIsDisorderDropdownOpen] = useState(false);
  
  // Search and dropdown states for regions
  const [regionSearchTerm, setRegionSearchTerm] = useState('');
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);

  const disorderDropdownRef = useRef(null);
  const regionDropdownRef = useRef(null);

  useEffect(() => {
    const uniqueDisorders = [...new Set(data.map(item => item.disorder))].filter(d => d);
    const uniqueRegions = [...new Set(data.map(item => item.region))].filter(r => r);
    
    setDisorders(uniqueDisorders);
    setRegions(uniqueRegions);
  }, [data]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (disorderDropdownRef.current && !disorderDropdownRef.current.contains(event.target)) {
        setIsDisorderDropdownOpen(false);
      }
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(event.target)) {
        setIsRegionDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredDisorders = disorders.filter(disorder =>
    disorder.toLowerCase().includes(disorderSearchTerm.toLowerCase())
  );

  const filteredRegions = regions.filter(region =>
    region.toLowerCase().includes(regionSearchTerm.toLowerCase())
  );

  const handleDisorderSelect = (disorder) => {
    if (!selectedDisorders.includes(disorder)) {
      setSelectedDisorders(prev => [...prev, disorder]);
    }
    setDisorderSearchTerm('');
    setIsDisorderDropdownOpen(false);
  };

  const handleRegionSelect = (region) => {
    if (!selectedRegions.includes(region)) {
      setSelectedRegions(prev => [...prev, region]);
    }
    setRegionSearchTerm('');
    setIsRegionDropdownOpen(false);
  };

  const removeDisorder = (disorderToRemove) => {
    setSelectedDisorders(prev => prev.filter(d => d !== disorderToRemove));
  };

  const removeRegion = (regionToRemove) => {
    setSelectedRegions(prev => prev.filter(r => r !== regionToRemove));
  };

  // Select all disorders (all or filtered)
  const selectAllDisorders = () => {
    const disordersToSelect = disorderSearchTerm ? filteredDisorders : disorders;
    setSelectedDisorders(prev => {
      const combined = [...new Set([...prev, ...disordersToSelect])];
      return combined;
    });
    setDisorderSearchTerm('');
    setIsDisorderDropdownOpen(false);
  };

  // Select all regions (all or filtered)
  const selectAllRegions = () => {
    const regionsToSelect = regionSearchTerm ? filteredRegions : regions;
    setSelectedRegions(prev => {
      const combined = [...new Set([...prev, ...regionsToSelect])];
      return combined;
    });
    setRegionSearchTerm('');
    setIsRegionDropdownOpen(false);
  };

  // Select none (clear all disorders)
  const selectNoneDisorders = () => {
    setSelectedDisorders([]);
  };

  // Select none (clear all regions)
  const selectNoneRegions = () => {
    setSelectedRegions([]);
  };

  // Check if all filtered disorders are selected
  const areAllFilteredDisordersSelected = () => {
    const disordersToCheck = disorderSearchTerm ? filteredDisorders : disorders;
    return disordersToCheck.length > 0 && disordersToCheck.every(disorder => 
      selectedDisorders.includes(disorder)
    );
  };

  // Check if all filtered regions are selected
  const areAllFilteredRegionsSelected = () => {
    const regionsToCheck = regionSearchTerm ? filteredRegions : regions;
    return regionsToCheck.length > 0 && regionsToCheck.every(region => 
      selectedRegions.includes(region)
    );
  };

  // Toggle select all/none for filtered disorders
  const toggleSelectAllFilteredDisorders = () => {
    if (areAllFilteredDisordersSelected()) {
      const disordersToRemove = disorderSearchTerm ? filteredDisorders : disorders;
      setSelectedDisorders(prev => 
        prev.filter(disorder => !disordersToRemove.includes(disorder))
      );
    } else {
      selectAllDisorders();
    }
  };

  // Toggle select all/none for filtered regions
  const toggleSelectAllFilteredRegions = () => {
    if (areAllFilteredRegionsSelected()) {
      const regionsToRemove = regionSearchTerm ? filteredRegions : regions;
      setSelectedRegions(prev => 
        prev.filter(region => !regionsToRemove.includes(region))
      );
    } else {
      selectAllRegions();
    }
  };

  // Group biomarkers by region
  const groupBiomarkersByRegion = (biomarkers) => {
    const grouped = {};
    
    biomarkers.forEach(item => {
      if (!grouped[item.region]) {
        grouped[item.region] = [];
      }
      grouped[item.region].push(item);
    });
    
    return grouped;
  };

  const handleSearch = () => {
    if (selectedDisorders.length === 0 && selectedRegions.length === 0) {
      alert('Please select at least one disorder or region');
      return;
    }

    let filteredData = data;

    if (selectedDisorders.length > 0) {
      filteredData = filteredData.filter(item => selectedDisorders.includes(item.disorder));
    }

    if (selectedRegions.length > 0) {
      filteredData = filteredData.filter(item => selectedRegions.includes(item.region));
    }

    const comparisonResults = selectedDisorders.map(disorder => {
      const disorderData = filteredData.filter(item => item.disorder === disorder);
      
      const increasedBiomarkers = [...new Set(disorderData
        .filter(item => item.change === 'Increase')
        .map(item => ({
          biomarker: item.biomarker,
          region: item.region
        }))
      )];

      const decreasedBiomarkers = [...new Set(disorderData
        .filter(item => item.change === 'Decrease')
        .map(item => ({
          biomarker: item.biomarker,
          region: item.region
        }))
      )];

      return {
        disorder,
        increased: increasedBiomarkers,
        decreased: decreasedBiomarkers
      };
    });

    setResults(comparisonResults);
  };

  const clearResults = () => {
    setResults(null);
    setSelectedDisorders([]);
    setSelectedRegions([]);
    setDisorderSearchTerm('');
    setRegionSearchTerm('');
  };

  const clearAllDisorders = () => {
    setSelectedDisorders([]);
    setDisorderSearchTerm('');
  };

  const clearAllRegions = () => {
    setSelectedRegions([]);
    setRegionSearchTerm('');
  };

  const renderComparisonResults = () => {
    if (!results || results.length === 0) return null;
    
    return (
      <div className="comparison-results">
        <h3>Comparison Results</h3>
        {results.map((result) => (
          <div key={result.disorder} className="disorder-comparison">
            <h4 className="disorder-title">
              {result.disorder}
            </h4>
            
            <div className="comparison-grid">
              {/* Increased Biomarkers Section */}
              <div className="result-section">
                <h5>Increased Biomarkers (↑) - {result.increased.length}</h5>
                {result.increased.length > 0 ? (
                  <div className="results-grouped">
                    {Object.entries(groupBiomarkersByRegion(result.increased)).map(([region, biomarkers]) => (
                      <div key={`increased-${region}`} className="region-group">
                        <h6 className="region-title">{region} Region</h6>
                        <div className="results-table-container">
                          <table className="results-table">
                            <thead>
                              <tr>
                                <th>Biomarker</th>
                                <th>Brain Region</th>
                              </tr>
                            </thead>
                            <tbody>
                              {biomarkers.map((item, index) => (
                                <tr key={`${item.biomarker}-${index}`}>
                                  <td>{item.biomarker}</td>
                                  <td>{item.region}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-data">No increased biomarkers found</p>
                )}
              </div>

              {/* Decreased Biomarkers Section */}
              <div className="result-section">
                <h5>Decreased Biomarkers (↓) - {result.decreased.length}</h5>
                {result.decreased.length > 0 ? (
                  <div className="results-grouped">
                    {Object.entries(groupBiomarkersByRegion(result.decreased)).map(([region, biomarkers]) => (
                      <div key={`decreased-${region}`} className="region-group">
                        <h6 className="region-title">{region} Region</h6>
                        <div className="results-table-container">
                          <table className="results-table">
                            <thead>
                              <tr>
                                <th>Biomarker</th>
                                <th>Brain Region</th>
                              </tr>
                            </thead>
                            <tbody>
                              {biomarkers.map((item, index) => (
                                <tr key={`${item.biomarker}-${index}`}>
                                  <td>{item.biomarker}</td>
                                  <td>{item.region}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-data">No decreased biomarkers found</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="search-section">
      <h2>Search by Disorder and Region</h2>
      
      <div className="search-controls">
        {/* Disorders Selection */}
        <div className="form-group">
          <label>Select Disorders:</label>
          <div className="biomarker-select-container" ref={disorderDropdownRef}>
            <div className="biomarker-input-wrapper">
              <input
                type="text"
                value={disorderSearchTerm}
                onChange={(e) => {
                  setDisorderSearchTerm(e.target.value);
                  setIsDisorderDropdownOpen(true);
                }}
                onFocus={() => setIsDisorderDropdownOpen(true)}
                placeholder="Type to search disorders..."
                className="biomarker-search-input"
              />
              {isDisorderDropdownOpen && (
                <div className="biomarker-dropdown">
                  <div className="biomarker-bulk-actions">
                    <button
                      type="button"
                      onClick={toggleSelectAllFilteredDisorders}
                      className="bulk-action-btn"
                    >
                      {areAllFilteredDisordersSelected() ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedDisorders.length > 0 && (
                      <button
                        type="button"
                        onClick={selectNoneDisorders}
                        className="bulk-action-btn"
                      >
                        Select None
                      </button>
                    )}
                  </div>
                  
                  {filteredDisorders.length > 0 ? (
                    filteredDisorders.map(disorder => (
                      <div
                        key={disorder}
                        className={`biomarker-option ${
                          selectedDisorders.includes(disorder) ? 'selected' : ''
                        }`}
                        onClick={() => handleDisorderSelect(disorder)}
                      >
                        {disorder}
                        {selectedDisorders.includes(disorder) && (
                          <span className="checkmark">✓</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="no-options">No disorders found</div>
                  )}
                </div>
              )}
            </div>

            {selectedDisorders.length > 0 && (
              <div className="selected-biomarkers">
                <div className="selected-header">
                  <span>Selected Disorders ({selectedDisorders.length})</span>
                  <div className="selected-actions">
                    <button 
                      type="button" 
                      onClick={selectAllDisorders}
                      className="action-btn secondary"
                      title="Select all disorders"
                    >
                      Select All
                    </button>
                    <button 
                      type="button" 
                      onClick={clearAllDisorders}
                      className="clear-all-btn"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <div className="selected-tags">
                  {selectedDisorders.map(disorder => (
                    <span key={disorder} className="selected-tag">
                      {disorder}
                      <button 
                        type="button" 
                        onClick={() => removeDisorder(disorder)}
                        className="remove-tag"
                        title="Remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Regions Selection */}
        <div className="form-group">
          <label>Select Brain Regions:</label>
          <div className="biomarker-select-container" ref={regionDropdownRef}>
            <div className="biomarker-input-wrapper">
              <input
                type="text"
                value={regionSearchTerm}
                onChange={(e) => {
                  setRegionSearchTerm(e.target.value);
                  setIsRegionDropdownOpen(true);
                }}
                onFocus={() => setIsRegionDropdownOpen(true)}
                placeholder="Type to search regions..."
                className="biomarker-search-input"
              />
              {isRegionDropdownOpen && (
                <div className="biomarker-dropdown">
                  <div className="biomarker-bulk-actions">
                    <button
                      type="button"
                      onClick={toggleSelectAllFilteredRegions}
                      className="bulk-action-btn"
                    >
                      {areAllFilteredRegionsSelected() ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedRegions.length > 0 && (
                      <button
                        type="button"
                        onClick={selectNoneRegions}
                        className="bulk-action-btn"
                      >
                        Select None
                      </button>
                    )}
                  </div>
                  
                  {filteredRegions.length > 0 ? (
                    filteredRegions.map(region => (
                      <div
                        key={region}
                        className={`biomarker-option ${
                          selectedRegions.includes(region) ? 'selected' : ''
                        }`}
                        onClick={() => handleRegionSelect(region)}
                      >
                        {region}
                        {selectedRegions.includes(region) && (
                          <span className="checkmark">✓</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="no-options">No regions found</div>
                  )}
                </div>
              )}
            </div>

            {selectedRegions.length > 0 && (
              <div className="selected-biomarkers">
                <div className="selected-header">
                  <span>Selected Regions ({selectedRegions.length})</span>
                  <div className="selected-actions">
                    <button 
                      type="button" 
                      onClick={selectAllRegions}
                      className="action-btn secondary"
                      title="Select all regions"
                    >
                      Select All
                    </button>
                    <button 
                      type="button" 
                      onClick={clearAllRegions}
                      className="clear-all-btn"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <div className="selected-tags">
                  {selectedRegions.map(region => (
                    <span key={region} className="selected-tag">
                      {region}
                      <button 
                        type="button" 
                        onClick={() => removeRegion(region)}
                        className="remove-tag"
                        title="Remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="button-group">
          <button onClick={handleSearch} className="search-btn">
            Search ({selectedDisorders.length + selectedRegions.length})
          </button>
          <button onClick={clearResults} className="clear-btn">Clear All</button>
        </div>
      </div>

      <div className="results">
        {renderComparisonResults()}
      </div>
    </div>
  );
};

export default DisorderRegionSearch;